import type { Config } from '@dashboard/types/config'

/** Flow and widget stamps returned by a draft save (`modified_at`), keyed by entity ID. */
export type ModifiedAtStamps = {
  flows?: Record<string, number>
  widgets?: Record<string, number>
}

/**
 * Copy the server's stamps onto the local config so this tab can sort by them
 * without a refetch. Only `modifiedAt` changes, which the dirty check ignores,
 * so applying them never autosaves.
 */
export function applyModifiedAtStamps(config: Config, stamps: ModifiedAtStamps | undefined): void {
  const collections = [
    [config.rightCta?.offerFunnels, stamps?.flows],
    [config.rightCta?.widgets, stamps?.widgets]
  ] as const
  for (const [entities, byId] of collections) {
    for (const entity of entities ?? []) {
      const stamp = byId?.[entity.id]
      if (typeof stamp === 'number' && entity.modifiedAt !== stamp) {
        entity.modifiedAt = stamp
      }
    }
  }
}

/**
 * Last-modified ordering for index views. Unstamped (legacy) entities sort last
 * when descending and first when ascending; ties fall back to name A–Z.
 */
export function compareByModifiedAt(
  a: { name: string; modifiedAt?: number },
  b: { name: string; modifiedAt?: number },
  dir: 'asc' | 'desc'
): number {
  const delta = (a.modifiedAt ?? 0) - (b.modifiedAt ?? 0)
  return (dir === 'asc' ? delta : -delta) || a.name.localeCompare(b.name)
}
